<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Bot, Loader2, MessageSquarePlus, Send, Sparkles } from "lucide-vue-next";
import { v3 } from "@/services/v3";
import { useBriefStore } from "@/stores/briefs";
import MarkdownContent from "@/components/MarkdownContent.vue";
import SkillFlowMap from "@/modules/agent/SkillFlowMap.vue";

const emit = defineEmits<{ openBrief: [id: number] }>();
const { t } = useI18n();
const briefs = useBriefStore();
const sessions = ref<any[]>([]);
const activeSession = ref<any>(null);
const agentInput = ref("");
const busy = ref(false);
const message = ref("");
const chatPane = ref<HTMLElement | null>(null);
const activeSkillId = computed(
  () => activeSession.value?.request_skill_id || "partner-service-router",
);
const activeSkillStage = computed(
  () => activeSession.value?.request_skill_stage || "",
);
const skillFlowComplete = computed(
  () => activeSession.value?.status === "ticket_created",
);

const quickPrompts = [
  "partnerAgent.promptMaterial",
  "partnerAgent.promptFeature",
  "partnerAgent.promptIssue",
  "partnerAgent.promptBusiness",
];

async function loadSessions() {
  sessions.value = (await v3.partnerAgentSessions()).sessions;
}

async function createSession(requestId?: number) {
  busy.value = true;
  message.value = "";
  try {
    activeSession.value = (
      await v3.createPartnerAgentSession(
        requestId ? { requestId, title: t("partnerAgent.followUp") } : {},
      )
    ).session;
    await loadSessions();
    await scrollLatest();
  } catch (error: any) {
    message.value = error.message || t("partnerAgent.createFailed");
  } finally {
    busy.value = false;
  }
}

async function openSession(id: number) {
  busy.value = true;
  message.value = "";
  try {
    activeSession.value = (await v3.partnerAgentSession(id)).session;
    await scrollLatest();
  } catch (error: any) {
    message.value = error.message || t("partnerAgent.loadFailed");
  } finally {
    busy.value = false;
  }
}

async function scrollLatest() {
  await nextTick();
  if (chatPane.value) chatPane.value.scrollTop = chatPane.value.scrollHeight;
}

async function sendAgent(content = agentInput.value) {
  const value = content.trim();
  if (value.length < 2 || busy.value) return;
  if (!activeSession.value || activeSession.value.status !== "active") {
    await createSession();
  }
  if (!activeSession.value) return;
  agentInput.value = "";
  activeSession.value.messages.push({
    id: -Date.now(),
    role: "user",
    content: value,
    created_at: new Date().toISOString(),
  });
  busy.value = true;
  message.value = "";
  await scrollLatest();
  try {
    const result = await v3.sendPartnerAgentMessage(activeSession.value.id, value);
    activeSession.value = result.session;
    await loadSessions();
    await scrollLatest();
    if (result.briefId) {
      await briefs.open(result.briefId);
      await briefs.sendRequirement(result.handoffPrompt || value);
      emit("openBrief", result.briefId);
    }
  } catch (error: any) {
    message.value = error.message || t("partnerAgent.sendFailed");
    activeSession.value = (
      await v3.partnerAgentSession(activeSession.value.id)
    ).session;
  } finally {
    busy.value = false;
    await scrollLatest();
  }
}

onMounted(async () => {
  try {
    await loadSessions();
    const active = sessions.value.find((item) => item.status === "active");
    if (active) await openSession(active.id);
    else await createSession();
  } catch (error: any) {
    message.value = error.message || t("partnerAgent.loadFailed");
  }
});
</script>

<template>
  <div class="client-page">
    <header class="page-head">
      <div>
        <h1>{{ t("newBrief") }}</h1>
        <p>{{ t("partnerAgent.intro") }}</p>
      </div>
      <button class="btn btn--primary" :disabled="busy" @click="createSession()">
        <MessageSquarePlus :size="16" />{{ t("partnerAgent.newConversation") }}
      </button>
    </header>
    <p v-if="message" class="notice">{{ message }}</p>

    <main class="brief-layout">
      <aside class="session-list" :aria-label="t('partnerAgent.history')">
        <small class="session-list__title">{{ t("partnerAgent.history") }}</small>
        <button
          v-for="item in sessions"
          :key="item.id"
          :class="{ active: activeSession?.id === item.id }"
          @click="openSession(item.id)"
        >
          <b>{{ item.request_title || item.title }}</b>
          <small>{{
            item.status === "active"
              ? t("partnerAgent.discussing")
              : item.status === "ticket_created"
                ? t("partnerAgent.ticketCreated")
                : t("partnerAgent.projectCreated")
          }}</small>
        </button>
      </aside>

      <section class="conversation">
        <header>
          <Bot :size="20" />
          <div>
            <b>Brief Agent</b>
            <small>{{ t("partnerAgent.agentHint") }}</small>
          </div>
        </header>
        <div ref="chatPane" class="messages">
          <div v-if="!activeSession?.messages?.length" class="welcome">
            <Sparkles :size="24" />
            <h2>{{ t("partnerAgent.welcome") }}</h2>
            <p>{{ t("partnerAgent.welcomeHint") }}</p>
            <button
              v-for="prompt in quickPrompts"
              :key="prompt"
              @click="sendAgent(t(prompt))"
            >
              {{ t(prompt) }}
            </button>
          </div>
          <article
            v-for="item in activeSession?.messages || []"
            :key="item.id"
            :class="item.role"
          >
            <small>{{ item.role === "user" ? t("brief.you") : "Brief Agent" }}</small>
            <MarkdownContent v-if="item.role === 'assistant'" :content="item.content" />
            <p v-else>{{ item.content }}</p>
          </article>
          <div v-if="busy" class="thinking">
            <Loader2 class="spin" :size="16" />{{ t("partnerAgent.processing") }}
          </div>
        </div>
        <footer>
          <textarea
            v-model="agentInput"
            class="textarea"
            :placeholder="t('brief.inputPlaceholder')"
            @keydown.ctrl.enter.prevent="sendAgent()"
          />
          <button
            class="btn btn--primary"
            :disabled="busy || agentInput.trim().length < 2"
            @click="sendAgent()"
          >
            <Send :size="17" />{{ t("send") }}
          </button>
        </footer>
      </section>
    </main>
    <SkillFlowMap
      v-if="activeSession"
      :skill-id="activeSkillId"
      :current-stage="activeSkillStage"
      :running="busy"
      :complete="skillFlowComplete"
    />
  </div>
</template>

<style scoped>
.client-page {
  display: flex;
  height: calc(100dvh - 64px);
  min-height: 0;
  flex-direction: column;
  overflow: hidden;
  background: #f5f7f7;
  color: var(--ink);
}
.page-head {
  display: flex;
  flex: 0 0 auto;
  align-items: flex-start;
  justify-content: space-between;
  gap: 14px;
  padding: 26px 30px 18px;
  border-bottom: 1px solid var(--line);
  background: #fff;
}
.page-head h1 { margin: 0; font-size: 22px; }
.page-head p { margin: 5px 0 0; color: var(--muted); }
.page-head .btn { display: flex; align-items: center; gap: 6px; white-space: nowrap; }
.notice {
  margin: 0;
  padding: 10px 30px;
  border-bottom: 1px solid #f0d1ba;
  background: #fff8f3;
  color: var(--brand-dark);
}
.brief-layout {
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr);
  flex: 1;
  min-height: 0;
  background: #fff;
}
.session-list {
  min-height: 0;
  overflow-y: auto;
  border-right: 1px solid var(--line);
  padding: 16px 10px;
  background: #fbfcfc;
}
.session-list__title { display: block; padding: 0 8px 9px; color: var(--muted); }
.session-list button {
  display: grid;
  width: 100%;
  gap: 3px;
  border: 0;
  padding: 11px 10px;
  background: transparent;
  color: var(--ink);
  text-align: left;
}
.session-list button.active { background: #fff0e7; box-shadow: inset 3px 0 var(--brand); }
.session-list button b { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.session-list button small { color: var(--muted); }
.conversation {
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  min-width: 0;
  min-height: 0;
}
.conversation > header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
}
.conversation > header b, .conversation > header small { display: block; }
.conversation > header small { margin-top: 2px; color: var(--muted); }
.messages {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 24px 30px;
}
.messages article {
  max-width: min(760px, 78%);
  margin: 0 0 14px;
  padding: 12px 14px;
  border: 1px solid var(--line);
  background: #fff;
  line-height: 1.65;
}
.messages article.user { margin-left: auto; border-color: #f2c7a8; background: #fff0e7; }
.messages article small { display: block; margin-bottom: 4px; color: var(--muted); }
.messages article p { margin: 0; white-space: pre-wrap; }
.welcome { display: grid; justify-items: center; max-width: 620px; margin: 45px auto; gap: 8px; text-align: center; }
.welcome h2, .welcome p { margin: 0; }
.welcome p { color: var(--muted); }
.welcome button { width: 100%; border: 1px solid var(--line); padding: 10px; background: #fff; text-align: left; }
.thinking { display: flex; align-items: center; gap: 7px; color: var(--muted); }
.conversation > footer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 10px;
  padding: 14px 30px;
  border-top: 1px solid var(--line);
  background: #fff;
}
.conversation > footer .textarea { min-height: 72px; }
.conversation > footer .btn { display: flex; align-items: center; gap: 6px; }
.spin { animation: spin 1s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 700px) {
  .client-page {
    height: calc(100dvh - var(--mobile-topbar-height, 32px) - 62px - env(safe-area-inset-bottom));
  }
  .page-head { align-items: center; padding: 10px 14px; }
  .page-head h1 { font-size: 18px; }
  .page-head p { display: none; }
  .page-head .btn { min-height: 40px; padding: 8px 10px; }
  .notice { padding: 8px 14px; }
  .brief-layout { grid-template-columns: 1fr; grid-template-rows: auto minmax(0, 1fr); }
  .session-list {
    display: flex;
    gap: 5px;
    overflow-x: auto;
    border: 0;
    border-bottom: 1px solid var(--line);
    padding: 6px 8px;
  }
  .session-list__title { display: none; }
  .session-list button { flex: 0 0 148px; padding: 8px; }
  .conversation > header { padding: 10px 14px; }
  .conversation > header small { display: none; }
  .messages { padding: 12px 14px; }
  .messages article { max-width: 92%; }
  .welcome { margin: 20px auto; }
  .conversation > footer { grid-template-columns: 1fr; padding: 9px 12px; }
  .conversation > footer .textarea { min-height: 46px; }
  .conversation > footer .btn { justify-content: center; }
}
</style>
